import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

const MENU_WIDTH = 208;
const VIEWPORT_MARGIN = 8;

interface ActionsDropdownMenuProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

export const ActionsDropdownMenu: React.FC<ActionsDropdownMenuProps> = ({
  open,
  onToggle,
  onClose,
  children,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const ignoreNextCloseRef = useRef(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const recomputePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 200;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;

    let top = rect.bottom + 4;
    if (top + menuHeight > viewportH - VIEWPORT_MARGIN) {
      const above = rect.top - menuHeight - 4;
      top =
        above >= VIEWPORT_MARGIN
          ? above
          : Math.max(VIEWPORT_MARGIN, viewportH - menuHeight - VIEWPORT_MARGIN);
    }
    let left = rect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + MENU_WIDTH > viewportW - VIEWPORT_MARGIN) {
      left = viewportW - MENU_WIDTH - VIEWPORT_MARGIN;
    }

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) recomputePosition();
    else setPos(null);
  }, [open, recomputePosition, children]);

  useEffect(() => {
    if (!open) return;

    const handleDocMouseDown = (e: MouseEvent) => {
      if (ignoreNextCloseRef.current) {
        ignoreNextCloseRef.current = false;
        return;
      }
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleViewportChange = () => recomputePosition();

    document.addEventListener('mousedown', handleDocMouseDown);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, onClose, recomputePosition]);

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          ignoreNextCloseRef.current = true;
          onToggle();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
            }}
            className="z-[200] origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-lg ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
};

interface ActionMenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

const menuItemClass = (tone: 'default' | 'danger' = 'default', disabled = false) =>
  `flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
    tone === 'danger'
      ? 'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20'
      : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800'
  }`;

export const ActionMenuItem: React.FC<ActionMenuItemProps> = ({
  icon,
  label,
  onClick,
  tone = 'default',
  disabled = false,
}) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onClick={onClick}
    className={menuItemClass(tone, disabled)}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);

export const ActionMenuDivider: React.FC = () => (
  <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
);
